import React, { useCallback, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import {
    shouldShowNotOnWhatsAppBanner,
    type CampaignFailureBreakdown,
} from '@common-components/message-status/campaign-failure-breakdown';

interface NotOnWhatsAppBannerProps {
    campaignId: string;
    breakdown: CampaignFailureBreakdown | undefined | null;
    /** Narrow the recipient list to the not-on-WhatsApp failures ("View these numbers"). */
    onViewNumbers: () => void;
}

const dismissKey = (campaignId: string) => `bt.notOnWaBanner.dismissed.${campaignId}`;

function readDismissed(campaignId: string): boolean {
    try {
        return localStorage.getItem(dismissKey(campaignId)) === '1';
    } catch {
        return false; // private window / blocked storage → show the banner
    }
}

/**
 * BE#710 — campaign-level hint when a meaningful share of the failures are "recipient not on
 * WhatsApp" (usually a missing country code). Count comes from the server-side aggregate
 * (`campaign-failure-breakdown`), NOT the lazy-loaded rows, so it is accurate on a long campaign.
 * Dismiss is a per-viewer convenience only (localStorage) — the banner renders correctly without it.
 */
export const NotOnWhatsAppBanner: React.FC<NotOnWhatsAppBannerProps> = ({ campaignId, breakdown, onViewNumbers }) => {
    const { t } = useTranslation(namespace);
    const [dismissed, setDismissed] = useState<boolean>(() => readDismissed(campaignId));

    const handleDismiss = useCallback(() => {
        setDismissed(true);
        try {
            localStorage.setItem(dismissKey(campaignId), '1');
        } catch {
            /* non-persistent dismiss is fine */
        }
    }, [campaignId]);

    if (dismissed || !shouldShowNotOnWhatsAppBanner(breakdown)) return null;

    const count = breakdown?.notOnWhatsAppCount ?? 0;

    return (
        <div
            role="status"
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '12px 14px',
                marginBottom: 14,
                borderRadius: 10,
                border: '1px solid var(--line)',
                background: 'var(--surface)',
                borderInlineStart: '3px solid #f59e0b',
            }}
        >
            <AlertTriangle size={18} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--ink)', lineHeight: 1.45 }}>
                    {t('campaignProgress.notOnWhatsAppHint', { count })}
                </p>
                <button
                    type="button"
                    onClick={onViewNumbers}
                    style={{
                        marginTop: 6,
                        padding: 0,
                        border: 'none',
                        background: 'none',
                        color: 'var(--primary, #2563eb)',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        font: 'inherit',
                    }}
                >
                    {t('campaignProgress.notOnWhatsAppHintAction')}
                </button>
            </div>
            <button
                type="button"
                onClick={handleDismiss}
                aria-label={t('campaignProgress.notOnWhatsAppHintDismiss')}
                style={{
                    flexShrink: 0,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: 'var(--muted)',
                    padding: 2,
                    lineHeight: 0,
                }}
            >
                <X size={16} />
            </button>
        </div>
    );
};

export default NotOnWhatsAppBanner;
